import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Truck, Move3D, Package, Warehouse } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselApi,
} from '@/components/ui/carousel';

const AutoDockInAction = (): JSX.Element => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const activeIndexRef = useRef<number>(-1); // guard to avoid double-run on same slide

  // Track drag state to tame controls while swiping
  useEffect(() => {
    if (!api) return;

    const onDown = () => setIsDragging(true);
    const onUpLike = () => setTimeout(() => setIsDragging(false), 80);

    api.on('pointerDown', onDown);
    api.on('scroll', onDown);
    api.on('pointerUp', onUpLike);
    api.on('settle', onUpLike);

    return () => {
      api.off('pointerDown', onDown);
      api.off('scroll', onDown);
      api.off('pointerUp', onUpLike);
      api.off('settle', onUpLike);
    };
  }, [api]);

  // Autoplay current, pause others (without resetting currentTime)
  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      const newCurrent = api.selectedScrollSnap();

      // If Embla fires select again for the same index, ignore
      if (activeIndexRef.current === newCurrent) return;
      activeIndexRef.current = newCurrent;

      setCurrent(newCurrent);

      videoRefs.current.forEach((video, index) => {
        if (!video) return;
        try {
          if (index === newCurrent) {
            video.muted = true;            // iOS inline autoplay
            video.controls = !isDragging;  // avoid accidental taps while drag
            void video.play();
          } else {
            video.pause();
          }
        } catch {
          /* noop */
        }
      });
    };

    api.on('select', onSelect);
    onSelect(); // initial sync

    return () => {
      api.off('select', onSelect);
    };
  }, [api, isDragging]);

  // Autoplay first slide on mount
  useEffect(() => {
    const firstVideo = videoRefs.current[0];
    if (firstVideo) {
      firstVideo.volume = 0.1;
      firstVideo.muted = true;
      try {
        void firstVideo.play();
      } catch {
        /* noop */
      }
    }
  }, []);

  const setVideoRef = useCallback(
    (index: number) => (ref: HTMLVideoElement | null) => {
      videoRefs.current[index] = ref;
      if (ref) {
        ref.volume = 0.1;
        ref.muted = true;
        // Ensure inline playback on iOS/WebKit variants
        ref.setAttribute('playsinline', '');
        ref.setAttribute('webkit-playsinline', '');
        // Optional UX hints (some browsers ignore)
        ref.setAttribute('x5-playsinline', 'true');
        ref.setAttribute('x-webkit-airplay', 'allow');
        ref.setAttribute('controlsList', 'nodownload noremoteplayback');
      }
    },
    []
  );

  const steps = [
    {
      title: 'Truck Arrival & Docking',
      description:
        'The truck aligns and docks precisely at the loading bay to initiate the automated unloading sequence.',
      icon: Truck,
      videoUrl:
        'https://cdn.vegamrobotics.com/uploads/Step-01+Truck+Arrival+%26+Docking.mp4',
      duration: '0:15',
    },
    {
      title: 'AMR Alignment & Dock Preparation',
      description:
        'The Automated Mobile Robot (AMR)/AUTODock Head navigates and positions itself for seamless integration with the dock and unloading system.',
      icon: Move3D,
      videoUrl:
        'https://cdn.vegamrobotics.com/uploads/Step-02+AMR+Alignment+%26+Dock+Preparation.mp4',
      duration: '0:12',
    },
    {
      title: 'Automated Truck Unloading',
      description:
        'Advanced robotic systems unload cargo onto a powered flexible conveyor, eliminating manual handling and enhancing operational efficiency and safety.',
      icon: Package,
      videoUrl:
        'https://cdn.vegamrobotics.com/uploads/Step+3+Automated+Truck+Unloading.mp4',
      duration: '0:18',
    },
    {
      title: 'End-of-Line Handling & Automatic Stacking',
      description:
        'Items are transferred at the end of the line and automatically stacked for efficient storage and further processing.',
      icon: Warehouse,
      videoUrl:
        'https://cdn.vegamrobotics.com/uploads/Step+4+End-of-Line+Handling+%26+Automatic+Stacking.mp4',
      duration: '0:10',
    },
  ] as const;

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-12">
          <h2 className="text-4xl lg:text-5xl font-poppins font-bold mb-4 bg-gradient-to-r from-foreground to-primary bg-clip-text text-transparent leading-tight">
            AutoDock in Action
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            From Dock-to-Stock in one seamless flow — see AutoDock in action.
          </p>
        </div>

        {/* Full-bleed track on mobile + centered slide with peeking neighbors and spacing */}
        <div className="-mx-6 sm:mx-0">
          <Carousel
            setApi={setApi}
            className="w-full relative"
            opts={{ align: 'center', loop: true, containScroll: 'trimSnaps' }}
          >
            <CarouselContent className="-ml-4 sm:ml-0">
              {steps.map((step, index) => {
                const isCurrentSlide = current === index;
                return (
                  <CarouselItem
                    key={index}
                    className="
                      basis-[85%]              /* center current, peek neighbors on mobile */
                      sm:basis-4/5 md:basis-3/4 lg:basis-4/5
                      pl-4                     /* spacing between slides */
                    "
                  >
                    <div className="group cursor-pointer h-full">
                      {/* Video Card */}
                      <div
                        className="
                          relative aspect-video
                          rounded-2xl overflow-hidden
                          bg-gradient-to-br from-primary/20 via-primary/10 to-background
                          border border-border
                          shadow-xl group-hover:shadow-2xl transition-all duration-300
                          mb-6
                        "
                      >
                        <video
                          ref={setVideoRef(index)}
                          className="w-full h-full object-cover"
                          src={step.videoUrl}
                          controls={!isDragging}
                          preload="metadata"
                          muted
                          playsInline
                          loop
                        />
                        {/* Duration */}
                        <div className="absolute top-3 right-3 bg-black/70 px-3 py-1 rounded-full text-sm text-white font-medium backdrop-blur-sm">
                          {step.duration}
                        </div>
                        {/* Step */}
                        <div className="absolute top-3 left-3 bg-primary/90 text-primary-foreground px-3 py-1 rounded-full text-sm font-semibold backdrop-blur-sm">
                          Step {index + 1}
                        </div>
                        {/* Playing Indicator */}
                        {isCurrentSlide && (
                          <div className="absolute bottom-3 left-3 flex items-center gap-1">
                            <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                            <div className="w-2 h-2 bg-primary rounded-full animate-pulse [animation-delay:150ms]" />
                            <div className="w-2 h-2 bg-primary rounded-full animate-pulse [animation-delay:300ms]" />
                          </div>
                        )}
                      </div>

                      {/* Step Info */}
                      <div className="text-center px-2">
                        <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors duration-300">
                          {step.title}
                        </h3>
                        <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </CarouselItem>
                );
              })}
            </CarouselContent>

            {/* Arrows */}
  <CarouselPrevious
  className="
    absolute left-0 sm:left-4 top-1/2 -translate-y-1/2 z-20
    w-8 h-10 sm:w-12 sm:h-12 rounded-full
    flex items-center justify-center
    text-foreground hover:text-primary
    bg-background/90 hover:bg-background
    backdrop-blur-sm border border-border shadow-lg
  "
/>
<CarouselNext
  className="
    absolute right-0 sm:right-4 top-1/2 -translate-y-1/2 z-20
    w-8 h-10 sm:w-12 sm:h-12 rounded-full
    flex items-center justify-center
    text-foreground hover:text-primary
    bg-background/90 hover:bg-background
    backdrop-blur-sm border border-border shadow-lg
  "
/>


          </Carousel>
        </div>
      </div>
    </section>
  );
};

export default AutoDockInAction;
